import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./CartDrawer.module.css";
import { CartConfigInterface, CartData } from "../CartTypes";
import AngleRightIcon from "../../../components/Icons/AngleRightIcon";
import AngleLeftIcon from "../../../components/Icons/AngleLeftIcon";
import { DefaultImageIcon } from "./CartProducts";
import {
    ATTRIBUTE_KEY_SHORTCODES,
    CartItemPositions,
    fetchStoreProducts,
    getDiscountValueWithSign,
    getFbtProductRecommendations,
    getFinalPrice,
    getProductsDynamicallyAndReturnNewProductsArray,
    parseCTAWithIcon,
    updateBundleStats,
} from "../../Bundles/constants";
import { getPriceLabel } from "../../Bundles/constants";
import { ProductObj } from "../../Bundles/Types";
import Button from "../../Bundles/BundlesComponents/Button";
import config from "../../../config";
import useCartDrawerLayoutHook from "../../Bundles/useCartDrawerLayoutHook";

const PlainCarousel = ({
    products,
    money_format,
    currency,
    cartConfig,
    addProductToCart,
    setAddProductToCart,
    loading,
    standAlone,
    addToCartFromCart,
    setLoading,
    productNaviRef,
    upsellLayout,
}: {
    cartConfig: CartConfigInterface;
    products: ProductObj[];
    money_format: string;
    currency: string;
    setAddProductToCart?: any;
    addProductToCart: any;
    loading: number | null;
    standAlone: boolean;
    addToCartFromCart: any;
    setLoading: Dispatch<SetStateAction<number | null>>;
    productNaviRef: RefObject<HTMLElement>;
    upsellLayout: any;
}) => {
    // OLD: used currentIndex to swap which single product was displayed
    // NEW: currentIndex only tracks the active dot/arrow state — all products are always rendered
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    // OLD: swipeMe ref + SwipeDetector class for touch swipe
    // NEW: scrollRef — the container is natively scrollable, no SwipeDetector needed
    const scrollRef = useRef<HTMLDivElement>(null);
    const len = products.length;
    // OLD: checked these inline everywhere as cartConfig?.upsell?.upsell_layout_dir == "horizontal" etc.
    // NEW: extracted to reusable booleans — replaces the old "peakingCards" concept
    const isHorizontal = cartConfig?.upsell?.upsell_layout_dir == "horizontal";
    const isHeaderNav = upsellLayout?.productNaviBtnPosition == CartItemPositions.HEADER;

    // NEW: mouse drag state for desktop drag-to-scroll (OLD had no desktop drag support)
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const scrollStartLeft = useRef(0);
    // NEW: prevents accidental "Add to cart" click after a drag gesture
    const hasDragged = useRef(false);

    // OLD: used (currentIndex + i) % len to calculate which product to show, dot index derived from itemsPerView math
    // NEW: reads actual scroll position from DOM to determine which product is visible
    const updateCurrentIndexFromScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || !el.children.length) return;
        const children = Array.from(el.children) as HTMLElement[];
        const scrollLeft = el.scrollLeft;
        // Find the child whose left offset is closest to the current scrollLeft
        let closestIdx = 0;
        let closestDist = Infinity;
        children.forEach((child, idx) => {
            const dist = Math.abs(child.offsetLeft - scrollLeft);
            if (dist < closestDist) {
                closestDist = dist;
                closestIdx = idx;
            }
        });
        setCurrentIndex(closestIdx);
    }, []);

    // Scroll to a specific product index using the actual child element
    const scrollToIndex = useCallback((index: number) => {
        const el = scrollRef.current;
        if (!el) return;
        const child = el.children[index] as HTMLElement;
        if (!child) return;
        el.scrollTo({ left: child.offsetLeft, behavior: "smooth" });
    }, []);

    const nextSlide = useCallback(
        (e?: any) => {
            e?.preventDefault();
            e?.stopPropagation();
            if (addProductToCart?.id) return;
            const newIndex = Math.min(currentIndex + 1, len - 1);
            setCurrentIndex(newIndex);
            scrollToIndex(newIndex);
        },
        [currentIndex, len, addProductToCart, scrollToIndex],
    );

    const prevSlide = useCallback(
        (e?: any) => {
            e?.preventDefault();
            e?.stopPropagation();
            const newIndex = Math.max(currentIndex - 1, 0);
            setCurrentIndex(newIndex);
            scrollToIndex(newIndex);
        },
        [currentIndex, scrollToIndex],
    );

    // Mouse drag handlers for desktop
    const onPointerDown = useCallback((e: React.PointerEvent) => {
        const el = scrollRef.current;
        if (!el) return;
        // Don't initiate drag when clicking on the Add-to-Cart button (or anything inside it)
        const target = e.target as HTMLElement;
        if (target.closest(`.${styles.upsellAddToCart}`)) return;
        isDragging.current = true;
        hasDragged.current = false;
        dragStartX.current = e.clientX;
        scrollStartLeft.current = el.scrollLeft;
        el.classList.add(styles.dragging);
        el.setPointerCapture(e.pointerId);
    }, []);

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        if (!isDragging.current) return;
        const dx = e.clientX - dragStartX.current;
        if (Math.abs(dx) > 5) hasDragged.current = true;
        const el = scrollRef.current;
        if (el) el.scrollLeft = scrollStartLeft.current - dx;
    }, []);

    const onPointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (!isDragging.current) return;
            isDragging.current = false;
            const el = scrollRef.current;
            if (el) {
                el.classList.remove(styles.dragging);
                el.releasePointerCapture(e.pointerId);
            }
            updateCurrentIndexFromScroll();
            // Reset after drag ends so subsequent button clicks are not blocked
            hasDragged.current = false;
        },
        [updateCurrentIndexFromScroll],
    );

    // Listen to scroll events for dot sync
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        let timeout: ReturnType<typeof setTimeout>;
        const onScroll = () => {
            clearTimeout(timeout);
            timeout = setTimeout(updateCurrentIndexFromScroll, 100);
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            el.removeEventListener("scroll", onScroll);
            clearTimeout(timeout);
        };
    }, [updateCurrentIndexFromScroll]);

    // Header navigation arrows (for HEADER position mode)
    useEffect(() => {
        if (productNaviRef?.current && isHeaderNav && products.length > 1) {
            const leftSpan = document.createElement("span");
            leftSpan.style.cursor = "pointer";
            if (currentIndex <= 0) {
                leftSpan.classList.add(styles.disabledArrow);
            } else {
                leftSpan.onclick = prevSlide;
            }

            const leftIcon = document.createElement("div");
            ReactDOM.render(
                <AngleLeftIcon size={15} color={cartConfig?.upsell_design?.upsell_button_bg_color} />,
                leftIcon,
            );
            leftSpan.appendChild(leftIcon);
            productNaviRef.current.appendChild(leftSpan);

            let rightSpan: HTMLSpanElement | null = null;
            if (len >= 2) {
                rightSpan = document.createElement("span");
                rightSpan.style.cursor = "pointer";
                if (currentIndex >= len - 1) {
                    rightSpan.classList.add(styles.disabledArrow);
                } else {
                    rightSpan.onclick = nextSlide;
                }

                const rightIcon = document.createElement("div");
                ReactDOM.render(
                    <AngleRightIcon size={15} color={cartConfig?.upsell_design?.upsell_button_bg_color} />,
                    rightIcon,
                );
                rightSpan.appendChild(rightIcon);
                productNaviRef.current.appendChild(rightSpan);
            }

            return () => {
                if (productNaviRef?.current?.contains(leftSpan)) {
                    productNaviRef.current.removeChild(leftSpan);
                }
                if (rightSpan && productNaviRef?.current?.contains(rightSpan)) {
                    productNaviRef.current.removeChild(rightSpan);
                }
            };
        }
    }, [
        productNaviRef,
        len,
        currentIndex,
        isHeaderNav,
        cartConfig?.upsell_design?.upsell_button_bg_color,
        products,
        prevSlide,
        nextSlide,
    ]);

    return (
        <>
            <div className={styles.slider} ref={containerRef}>
                {upsellLayout?.productNaviBtnPosition == CartItemPositions.DEFAULT && len >= 2 && (
                    <span onClick={prevSlide} style={{ cursor: "pointer" }}>
                        <AngleLeftIcon size={15} color={cartConfig?.upsell_design?.upsell_title_color} />
                    </span>
                )}
                <div
                    className={styles.slideContainer}
                    ref={scrollRef}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    style={{
                        ...(products?.length <= 1 && { paddingInline: 15 }),
                    }}
                >
                    {products.map((product, productIndex) => {
                        const imageUrl =
                            product?.variants?.[0]?.image?.originalSrc || product?.images?.[0]?.originalSrc;

                        return (
                            <div
                                key={product?.id || productIndex}
                                className={`${styles.slideItem} ${isHorizontal ? styles.horizontal : ""}`}
                                style={{
                                    background: cartConfig?.upsell_design?.upsell_product_bg_color,
                                    border: `1px solid ${cartConfig?.upsell_design?.upsell_product_border_color}`,
                                    borderRadius: cartConfig?.upsell_design?.upsell_card_radius,
                                    ...(products.length === 1 && { minWidth: "100%", width: "100%" }),
                                }}
                            >
                                <div
                                    className={styles.upsellItemImageWrapper}
                                    style={{
                                        maxWidth: isHorizontal && isHeaderNav ? 64 : "",
                                        maxHeight: isHorizontal && isHeaderNav ? 64 : "",
                                    }}
                                    title={product?.title}
                                >
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt={product.handle}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                padding: 2,
                                                objectFit: "cover",
                                                objectPosition: "center",
                                                borderRadius: 8,
                                            }}
                                        />
                                    ) : (
                                        <DefaultImageIcon />
                                    )}
                                </div>
                                <div className={styles.upsellItemDetails} style={{ gap: 15 }}>
                                    <div
                                        className={styles.upsellItemTitle}
                                        style={{
                                            color: cartConfig?.upsell_design?.upsell_product_text_color,
                                            fontSize: cartConfig?.upsell_design?.upsell_product_title_size,
                                            fontWeight:
                                                cartConfig?.upsell_design?.upsell_product_title_style?.split(" ")[0],
                                            fontStyle:
                                                cartConfig?.upsell_design?.upsell_product_title_style?.split(" ")[1],
                                        }}
                                        title={product?.title}
                                    >
                                        {product?.title}
                                    </div>
                                    <div
                                        className={styles.upsellItemPriceWrapper}
                                        style={{
                                            color: cartConfig?.upsell_design?.upsell_product_text_color,
                                            ...(isHorizontal &&
                                                isHeaderNav &&
                                                products.length > 1 && {
                                                    gap: 5,
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    width: "100%",
                                                }),
                                        }}
                                    >
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: 5,
                                                    alignItems: "center",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <span
                                                    className={styles.upsellItemPrice}
                                                    style={{
                                                        fontSize: cartConfig?.upsell_design?.upsell_price_size,
                                                        fontWeight:
                                                            cartConfig?.upsell_design?.upsell_price_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            cartConfig?.upsell_design?.upsell_price_style?.split(
                                                                " ",
                                                            )[1],
                                                    }}
                                                >
                                                    {getPriceLabel(
                                                        getFinalPrice(
                                                            product?.discountType ?? "",
                                                            product?.discountValue ?? 0,
                                                            +product?.variants[0]?.price,
                                                        ),
                                                        money_format,
                                                        currency,
                                                    )}
                                                </span>

                                                {!!product?.discountType?.length && product?.discountType != "none" && (
                                                    <span
                                                        className={styles.upsellItemPriceStrike}
                                                        style={{
                                                            fontSize:
                                                                cartConfig?.upsell_design?.upsell_compared_price_size,
                                                            color: cartConfig?.upsell_design?.upsell_product_text_color,
                                                            fontWeight:
                                                                cartConfig?.upsell_design?.upsell_compared_price_style?.split(
                                                                    " ",
                                                                )[0],
                                                            fontStyle:
                                                                cartConfig?.upsell_design?.upsell_compared_price_style?.split(
                                                                    " ",
                                                                )[1],
                                                        }}
                                                    >
                                                        {getPriceLabel(
                                                            product?.variants[0]?.price,
                                                            money_format,
                                                            currency,
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            {!!product?.discountType?.length && product?.discountType != "none" && (
                                                <div
                                                    className={styles.upsellItemDiscount}
                                                    style={{
                                                        color: cartConfig?.upsell_design
                                                            ?.upsell_product_discount_text_color,
                                                        fontSize: cartConfig?.upsell_design?.upsell_discount_size,
                                                        fontWeight:
                                                            cartConfig?.upsell_design?.upsell_discount_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            cartConfig?.upsell_design?.upsell_discount_style?.split(
                                                                " ",
                                                            )[1],
                                                    }}
                                                >
                                                    (
                                                    {getDiscountValueWithSign(
                                                        product?.discountType,
                                                        product?.discountValue,
                                                        currency,
                                                        money_format,
                                                    )}
                                                    )
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    className={`${styles.upsellAddToCart}  ${
                                        loading == productIndex && styles.upsellAddToCartLoading
                                    }`}
                                    title={parseCTAWithIcon(
                                        cartConfig?.add_upsell_text
                                            ? cartConfig.add_upsell_text
                                            : isHorizontal
                                              ? "Add"
                                              : "Add to cart",
                                        cartConfig?.upsell_cta_icon_url,
                                        20,
                                    )}
                                    onClick={(e) => {
                                        e?.preventDefault();
                                        e?.stopPropagation();
                                        if (hasDragged.current) return;
                                        if (standAlone) {
                                            if (product?.variants?.length > 1) {
                                                setAddProductToCart({
                                                    ...product,
                                                    selectedVariants: [product.variants[0]],
                                                });
                                            } else {
                                                setLoading(productIndex);
                                                addToCartFromCart(
                                                    [
                                                        {
                                                            ...product,
                                                            selectedVariants: [product.variants[0]],
                                                        },
                                                    ],
                                                    {
                                                        [ATTRIBUTE_KEY_SHORTCODES.__upsell]: {
                                                            [ATTRIBUTE_KEY_SHORTCODES.cart_upsell_offer_id]:
                                                                product?.cartUpsellId ?? "",
                                                        },
                                                    },
                                                    true,
                                                );
                                            }
                                        }
                                    }}
                                    spinnerSize={16}
                                    style={{
                                        borderRadius: cartConfig?.upsell_design?.upsell_button_radius,
                                        // @ts-expect-error hack
                                        "--buttonBg": cartConfig?.upsell_design?.upsell_button_bg_color,
                                        "--buttonText": cartConfig?.upsell_design?.upsell_button_text_color,
                                        "--buttonHoverBg": cartConfig?.upsell_design?.upsell_button_bg_hover_color,
                                        "--buttonHoverText": cartConfig?.upsell_design?.upsell_button_text_hover_color,
                                        "--buttonBorder": cartConfig?.upsell_design?.upsell_button_border_color,
                                        "--buttonHoverBorder":
                                            cartConfig?.upsell_design?.upsell_button_hover_border_color,
                                        padding: cartConfig?.add_upsell_text?.includes("{icon}") ? 6 : 8,
                                    }}
                                    loading={loading == productIndex}
                                    inheritLoadingColor={true}
                                />
                            </div>
                        );
                    })}
                </div>
                {upsellLayout?.productNaviBtnPosition == CartItemPositions.DEFAULT && len >= 2 && (
                    <span onClick={nextSlide} style={{ cursor: "pointer" }}>
                        <AngleRightIcon size={15} color={cartConfig?.upsell_design?.upsell_title_color} />
                    </span>
                )}
            </div>
            {/* Pagination Dots */}
            {upsellLayout?.productNaviBtnPosition == CartItemPositions.DEFAULT && len >= 2 && (
                <div className={styles.dotsWrapperContainer}>
                    <div className={styles.dotsWrapper}>
                        {products.map((_, idx) => (
                            <span
                                style={{
                                    background:
                                        idx === currentIndex ? cartConfig?.upsell_design?.upsell_title_color : "",
                                }}
                                key={idx}
                                title={`Go to slide ${idx + 1}`}
                                className={`${styles.dot} ${idx === currentIndex ? styles.activeDot : ""}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCurrentIndex(idx);
                                    scrollToIndex(idx);
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

const Stacked = ({
    products,
    money_format,
    currency,
    cartConfig,
    setAddProductToCart,
    loading,
    standAlone,
    addToCartFromCart,
    setLoading,
}: {
    cartConfig: CartConfigInterface;
    products: ProductObj[];
    money_format: string;
    currency: string;
    setAddProductToCart?: any;
    loading: number | null;
    addToCartFromCart: any;
    standAlone: boolean;
    setLoading: Dispatch<SetStateAction<number | null>>;
    upsellLayout: any;
}) => {
    return (
        <div className={styles.stackedList}>
            {products?.map((product, index) => {
                const imageUrl = product?.variants?.[0]?.image?.originalSrc || product?.images?.[0]?.originalSrc;
                return (
                    <div
                        key={product.id}
                        className={`${styles.slideItem} ${styles.horizontal}`}
                        style={{
                            background: cartConfig?.upsell_design?.upsell_product_bg_color,
                            border: `1px solid ${cartConfig?.upsell_design?.upsell_product_border_color}`,
                            borderRadius: cartConfig?.upsell_design?.upsell_button_radius,
                            marginBottom: index < products.length - 1 ? "12px" : 0,
                            width: "100%",
                            minWidth: "100%",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                flexGrow: 1,
                            }}
                        >
                            <div className={styles.upsellItemImageWrapper}>
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={product.handle}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            padding: 2,
                                            objectFit: "cover",
                                            objectPosition: "center",
                                            borderRadius: 8,
                                        }}
                                    />
                                ) : (
                                    <DefaultImageIcon w={"100%"} h={"100%"} />
                                )}
                            </div>
                            <div className={styles.upsellItemDetails}>
                                <div
                                    className={styles.upsellItemTitle}
                                    style={{
                                        minHeight: "max-content",
                                        color: cartConfig?.upsell_design?.upsell_product_text_color,
                                        fontSize: cartConfig?.upsell_design?.upsell_product_title_size,
                                        fontWeight:
                                            cartConfig?.upsell_design?.upsell_product_title_style?.split(" ")[0],
                                        fontStyle: cartConfig?.upsell_design?.upsell_product_title_style?.split(" ")[1],
                                    }}
                                    title={product?.title}
                                >
                                    {product?.title}
                                </div>
                                <div className={styles.upsellItemPriceWrapper}>
                                    <span
                                        className={styles.upsellItemPrice}
                                        style={{
                                            color: cartConfig?.upsell_design?.upsell_product_text_color,
                                            fontSize: cartConfig?.upsell_design?.upsell_price_size,
                                            fontWeight: cartConfig?.upsell_design?.upsell_price_style?.split(" ")[0],
                                            fontStyle: cartConfig?.upsell_design?.upsell_price_style?.split(" ")[1],
                                        }}
                                    >
                                        {getPriceLabel(
                                            getFinalPrice(
                                                product?.discountType ?? "",
                                                product?.discountValue ?? 0,
                                                +product?.variants[0]?.price,
                                            ),
                                            money_format,
                                            currency,
                                        )}
                                    </span>

                                    {!!product?.discountType?.length && product?.discountType != "none" && (
                                        <span
                                            className={styles.upsellItemPriceStrike}
                                            style={{
                                                color: cartConfig?.upsell_design?.upsell_product_text_color,
                                                fontSize: cartConfig?.upsell_design?.upsell_compared_price_size,
                                                fontWeight:
                                                    cartConfig?.upsell_design?.upsell_compared_price_style?.split(
                                                        " ",
                                                    )[0],
                                                fontStyle:
                                                    cartConfig?.upsell_design?.upsell_compared_price_style?.split(
                                                        " ",
                                                    )[1],
                                            }}
                                        >
                                            {getPriceLabel(product?.variants[0]?.price, money_format, currency)}
                                        </span>
                                    )}
                                </div>
                                {!!product?.discountType?.length && product?.discountType != "none" && (
                                    <div
                                        className={styles.upsellItemDiscount}
                                        style={{
                                            color: cartConfig?.upsell_design?.upsell_product_discount_text_color,
                                            fontSize: cartConfig?.upsell_design?.upsell_discount_size,
                                            fontWeight: cartConfig?.upsell_design?.upsell_discount_style?.split(" ")[0],
                                            fontStyle: cartConfig?.upsell_design?.upsell_discount_style?.split(" ")[1],
                                        }}
                                    >
                                        (
                                        {getDiscountValueWithSign(
                                            product?.discountType,
                                            product?.discountValue,
                                            money_format,
                                            currency,
                                        )}
                                        )
                                    </div>
                                )}
                            </div>
                        </div>
                        <Button
                            className={`${styles.upsellAddToCart} ${loading == index && styles.upsellAddToCartLoading}`}
                            title={parseCTAWithIcon(
                                cartConfig?.add_upsell_text
                                    ? cartConfig.add_upsell_text
                                    : cartConfig?.upsell?.upsell_layout_dir === "horizontal"
                                      ? "Add"
                                      : "Add to cart",
                                cartConfig?.upsell_cta_icon_url,
                                20,
                            )}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (standAlone) {
                                    if (product?.variants?.length > 1) {
                                        setAddProductToCart({ ...product, selectedVariants: [product.variants[0]] });
                                    } else {
                                        setLoading(index);
                                        addToCartFromCart(
                                            [{ ...product, selectedVariants: [product.variants[0]] }],
                                            {
                                                [ATTRIBUTE_KEY_SHORTCODES.__upsell]: {
                                                    [ATTRIBUTE_KEY_SHORTCODES.cart_upsell_offer_id]:
                                                        product?.cartUpsellId ?? "",
                                                },
                                            },
                                            true,
                                        );
                                    }
                                }
                            }}
                            spinnerSize={16}
                            style={{
                                borderRadius: cartConfig?.upsell_design?.upsell_button_radius,
                                // @ts-expect-error hack
                                "--buttonBg": cartConfig?.upsell_design?.upsell_button_bg_color,
                                "--buttonText": cartConfig?.upsell_design?.upsell_button_text_color,
                                "--buttonHoverBg": cartConfig?.upsell_design?.upsell_button_bg_hover_color,
                                "--buttonHoverText": cartConfig?.upsell_design?.upsell_button_text_hover_color,
                                "--buttonBorder": cartConfig?.upsell_design?.upsell_button_border_color,
                                "--buttonHoverBorder": cartConfig?.upsell_design?.upsell_button_hover_border_color,
                                padding: cartConfig?.add_upsell_text?.includes("{icon}") ? 6 : 8,
                            }}
                            loading={loading == index}
                            inheritLoadingColor={true}
                        />
                    </div>
                );
            })}
        </div>
    );
};

const UpsellsComponent = ({
    cartConfig,
    money_format,
    currency,
    setAddProductToCart,
    addProductToCart,
    sft,
    addToCartFromCart,
    standAlone,
    cart,
}: {
    cartConfig: CartConfigInterface;
    money_format: string;
    currency: string;
    setAddProductToCart?: any;
    addProductToCart: any;
    sft: string;
    standAlone: boolean;
    addToCartFromCart: any;
    cart: CartData;
}) => {
    const { upsellLayout } = useCartDrawerLayoutHook({ config: cartConfig });
    const [isAdding, setIsAdding] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [upsellProducts, setUpsellProducts] = useState<ProductObj[]>([]);
    const [addingProductToCart, setAddingProductToCart] = useState<number | null>(null);
    const productNaviRef = useRef<HTMLElement>(null);
    const [hideUpsell, setHideUpsell] = useState(false);

    const getUpsellData = async () => {
        try {
            setLoadingProducts(true);
            const initialData = await fetchStoreProducts(
                sft,
                standAlone,
                cartConfig?.upsell?.limit_upsells_in_cart ? cartConfig?.upsell?.max_upsell_items : 2,
            );
            setUpsellProducts(initialData);

            checkUpsellVisibility({
                cartItems: cart?.items,
                maxLimit: cartConfig?.upsell?.limit_visible_upsells_in_cart
                    ? cartConfig?.upsell?.visible_upsell_limit
                    : undefined,
            });
        } catch (error) {
            console.error("Error fetching AI FBT data:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const prevUpsellRef = useRef<ProductObj[] | null>(null);

    useEffect(() => {
        const haveProductsChanged = () => {
            const prev = prevUpsellRef.current || [];
            if (prev.length !== upsellProducts.length) return true;

            const prevIds = prev.map((p) => p.id).sort();
            const currentIds = upsellProducts.map((p) => p.id).sort();

            for (let i = 0; i < prevIds.length; i++) {
                if (prevIds[i] !== currentIds[i]) return true;
            }

            return false;
        };
        if (standAlone && upsellProducts.length > 0 && haveProductsChanged()) {
            updateBundleStats({
                id: null,
                key: "views",
                view_type: 2,
                shop: window.kefi_bundles?.shop,
                appName: config.appName,
                product_ids: upsellProducts
                    .map((p) => p.variants?.[0]?.id)
                    .filter(Boolean) // Remove any undefined/null values
                    .join(","),
            });
            prevUpsellRef.current = upsellProducts;
        }
    }, [upsellProducts, standAlone]);

    useEffect(() => {
        const getRecommendations = async () => {
            try {
                setUpsellProducts([]); // Reset upsell products when cart changes
                // Take the last two unique products from cart
                const cartProducts = [...new Map(cart?.items.map((item: any) => [item.product_id, item])).values()];
                if (cartProducts.length === 0) return; // No products to fetch recommendations for
                // Fetch recommendations
                const recommendations = await Promise.all(
                    cartProducts.map((item: any) =>
                        getFbtProductRecommendations(
                            sft,
                            "gid://shopify/Product/" + item.product_id,
                            { form_id: cartConfig?.cart_id || "123" } as any,
                            100,
                            true,
                            cartConfig?.upsell?.recommendation_algorithm,
                            true,
                        ),
                    ),
                );
                // Flatten, deduplicate and filter out cart products
                let uniqueProducts: ProductObj[] = [];
                if (!cartConfig?.upsell?.show_if_item_in_cart) {
                    uniqueProducts = Array.from(
                        new Map(recommendations.flat().map((product) => [product.id, product])).values(),
                    ).filter((product) => !cart?.items.some((item: any) => product.id.includes(item.product_id)));
                } else {
                    uniqueProducts = Array.from(
                        new Map(recommendations.flat().map((product) => [product.id, product])).values(),
                    );
                }
                if (cartConfig?.additional_product?.shipping_protection_id?.length) {
                    uniqueProducts = uniqueProducts.filter(
                        (product) => product.id != cartConfig?.additional_product?.shipping_protection_id,
                    );
                }
                if (cartConfig?.additional_product?.gift_wrapping_id?.length) {
                    uniqueProducts = uniqueProducts.filter(
                        (product) => product.id != cartConfig?.additional_product?.gift_wrapping_id,
                    );
                }

                if (cartConfig?.upsell.exclude_ai_recommendation_products?.length) {
                    uniqueProducts = uniqueProducts.filter(
                        (product: any) =>
                            product?.id &&
                            !cartConfig?.upsell.exclude_ai_recommendation_products.includes(
                                +product.id.split("/").pop(),
                            ),
                    );
                }

                if (cartConfig?.upsell?.limit_upsells_in_cart) {
                    uniqueProducts.splice(cartConfig?.upsell?.max_upsell_items as unknown as number);
                }

                const updatedUniqueProducts = await getProductsDynamicallyAndReturnNewProductsArray(
                    uniqueProducts,
                    sft,
                    true,
                );
                setLoadingProducts(false);

                setUpsellProducts(updatedUniqueProducts);
                checkUpsellVisibility({
                    cartItems: cart?.items,
                    maxLimit: cartConfig?.upsell?.limit_visible_upsells_in_cart
                        ? cartConfig?.upsell?.visible_upsell_limit
                        : undefined,
                });
            } catch (error) {
                setLoadingProducts(false);

                console.error("Error fetching recommendations:", error);
            }
        };

        if (!standAlone) {
            if (cartConfig?.upsell?.upsell_products_type == "all") {
                setLoadingProducts(false);
                getUpsellData();
            } else if (
                cartConfig?.upsell?.upsell_products_type == "manual" &&
                cartConfig?.upsell?.upsell_offers.length > 0
            ) {
                const VALID_INDEX = 0;
                const products: ProductObj[] = [...cartConfig.upsell.upsell_offers[VALID_INDEX].upsell_products];

                if (products.length) {
                    setUpsellProducts(
                        products.map((product: any) => ({
                            ...product,
                            discountType:
                                VALID_INDEX >= 0 ? cartConfig.upsell.upsell_offers?.[VALID_INDEX]?.discount_type : "",
                            discountValue:
                                VALID_INDEX >= 0 ? cartConfig.upsell.upsell_offers?.[VALID_INDEX]?.discount_value : 0,
                            cartUpsellId:
                                VALID_INDEX >= 0
                                    ? // @ts-expect-error hack
                                      cartConfig.upsell.upsell_offers?.[VALID_INDEX]?.cart_upsell_offer_id
                                    : "",
                        })),
                    );
                    checkUpsellVisibility({
                        cartItems: cart?.items,
                        maxLimit: cartConfig?.upsell?.limit_visible_upsells_in_cart
                            ? cartConfig?.upsell?.visible_upsell_limit
                            : undefined,
                    });
                } else {
                    setUpsellProducts([]);
                }
            } else {
                setUpsellProducts([]);
            }
            setLoadingProducts(false);
        } else if (
            cartConfig?.upsell?.upsell_products_type == "all" &&
            !!sft?.length &&
            cartConfig?.upsell?.recommendation_algorithm
        ) {
            getRecommendations();
        } else if (cartConfig?.upsell?.upsell_offers.length > 0) {
            setLoadingProducts(true);
            const matchedOffers: Array<{ index: number; products: ProductObj[] }> = [];

            const offers = cartConfig?.upsell?.upsell_offers || [];

            // Helper to check if a product is already in cart
            const isNotInCart = (product: any) =>
                !cart?.items.some((item) => product.id?.includes(item.product_id as unknown as string));

            // First, check for 'all' type discounts
            for (let i = 0; i < offers.length; i++) {
                const offer = offers[i];
                let isMatch = false;
                let filteredProducts: ProductObj[] = [];

                if (offer.upsell_offer_type === "all") {
                    filteredProducts = offer.upsell_products.filter(isNotInCart);
                    isMatch = filteredProducts.length > 0;
                } else {
                    const isTriggered = offer.trigger_products?.some((triggerProduct: any) =>
                        cart?.items?.some((cartItem: any) => triggerProduct.id.includes(cartItem.product_id)),
                    );

                    if (isTriggered) {
                        filteredProducts = offer.upsell_products.filter(isNotInCart);
                        isMatch = filteredProducts.length > 0;
                    }
                }

                if (isMatch) {
                    matchedOffers.push({ index: i, products: filteredProducts });
                }
            }

            let products: ProductObj[] = [];
            matchedOffers.forEach(({ index, products: offerProducts }) => {
                const offer = offers[index];
                products.push(
                    ...offerProducts.map((product: any) => ({
                        ...product,
                        discountType: offer.discount_type,
                        discountValue: offer.discount_value,
                        cartUpsellId: offer.cart_upsell_offer_id,
                    })),
                );
            });

            if (!cartConfig?.upsell?.show_if_item_in_cart) {
                products = Array.from(new Map(products.map((obj) => [obj.id, obj])).values()).filter(
                    (product) => !cart?.items.some((item: any) => product.id.includes(item.product_id)),
                );
            }
            if (cartConfig?.upsell?.limit_upsells_in_cart) {
                products.splice(cartConfig?.upsell?.max_upsell_items as unknown as number);
            }

            if (products.length) products = Array.from(new Map(products.map((item: any) => [item.id, item])).values());
            (async () => {
                const updatedUniqueProducts = await getProductsDynamicallyAndReturnNewProductsArray(
                    products,
                    sft,
                    true,
                );
                setLoadingProducts(false);
                setUpsellProducts(updatedUniqueProducts);
                checkUpsellVisibility(cart);
            })();
        } else {
            setLoadingProducts(false);
            setUpsellProducts([]);
        }
    }, [
        cart?.items,
        cartConfig?.upsell?.recommendation_algorithm,
        cartConfig?.upsell?.upsell_products_type,
        cartConfig?.upsell?.limit_upsells_in_cart,
        cartConfig?.upsell?.max_upsell_items,
        cartConfig?.upsell?.upsell_offers,
        cartConfig?.additional_product?.shipping_protection_id,
        cartConfig?.additional_product?.gift_wrapping_id,
    ]);

    useEffect(() => {
        setAddingProductToCart(null);
    }, [cart?.items]);

    useEffect(() => {
        console._log(typeof setIsAdding);
    }, [isAdding]);

    useEffect(() => {
        if (!loadingProducts) {
            checkUpsellVisibility({
                cartItems: cart?.items,
                maxLimit: cartConfig?.upsell?.limit_visible_upsells_in_cart
                    ? cartConfig?.upsell?.visible_upsell_limit
                    : undefined,
            });
        }
    }, [cart?.items, upsellProducts, loadingProducts]);

    function checkUpsellVisibility({ cartItems, maxLimit }: { cartItems: any; maxLimit?: number }) {
        const upsellInCartCount = countUpsellsInCart(cartItems);

        // If limit disabled , always show
        if (!maxLimit || maxLimit <= 0) {
            setHideUpsell(false);
            return true;
        }

        // Hide when limit reached
        if (upsellInCartCount >= maxLimit) {
            setHideUpsell(true);
            return false;
        }

        // Otherwise show
        setHideUpsell(false);
        return true;
    }

    function countUpsellsInCart(cartItems: any[] = []) {
        const upsellItemsInCart = cartItems.filter((ci) => {
            const raw = ci?.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd];
            if (!raw) return false;

            try {
                const parsed = JSON.parse(raw);
                // If it's a manual upsell, also check that a discount is applied
                const isManualUpsell = !cartConfig?.upsell?.recommendation_algorithm;
                const hasDiscount = isManualUpsell ? ci?.discounts?.length > 0 : true;

                return hasDiscount && parsed && parsed[ATTRIBUTE_KEY_SHORTCODES.__upsell];
            } catch {
                return false;
            }
        });
        return upsellItemsInCart.length;
    }


    return (
        !loadingProducts &&
        !hideUpsell &&
        !!upsellProducts.length && (
            <div
                className={styles.upsellWrapper}
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: cartConfig?.upsell_design?.upsell_bg_color,
                    padding: "10px 0px 15px 0px",
                    position: cartConfig.upsell.is_upsell_sticky ? "sticky" : "unset",
                    bottom: cartConfig.upsell.is_upsell_sticky
                        ? (document.querySelector("#cartFooterWrapper")?.clientHeight || 0) +
                              (document.querySelector("#additionalProductContainer")?.clientHeight || 0) || "100px"
                        : "unset",
                    zIndex: 1000,
                }}
            >
                <div className={styles.upsellHeader}>
                    <div
                        className={`${styles.qlContainer}`}
                        dangerouslySetInnerHTML={{
                            __html: cartConfig?.upsell?.upsell_title,
                        }}
                        style={{
                            width: "80%",
                            color: cartConfig?.upsell_design?.upsell_title_color,
                            fontSize: cartConfig?.upsell_design?.upsell_title_size,
                            fontStyle: cartConfig?.upsell_design?.upsell_title_style?.split(" ")[1],
                        }}
                    ></div>
                    <span
                        style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                        ref={productNaviRef}
                    ></span>
                </div>
                {cartConfig?.upsell?.upsell_layout == "carousel" && (
                    <PlainCarousel
                        cartConfig={cartConfig}
                        currency={currency}
                        money_format={money_format}
                        products={upsellProducts}
                        addProductToCart={addProductToCart}
                        setAddProductToCart={setAddProductToCart}
                        loading={addingProductToCart}
                        setLoading={setAddingProductToCart}
                        standAlone={standAlone}
                        addToCartFromCart={addToCartFromCart}
                        productNaviRef={productNaviRef}
                        upsellLayout={upsellLayout}
                    />
                )}
                {cartConfig?.upsell?.upsell_layout == "stacked" && (
                    <Stacked
                        cartConfig={cartConfig}
                        currency={currency}
                        money_format={money_format}
                        products={upsellProducts}
                        setAddProductToCart={setAddProductToCart}
                        loading={addingProductToCart}
                        setLoading={setAddingProductToCart}
                        standAlone={standAlone}
                        addToCartFromCart={addToCartFromCart}
                        upsellLayout={upsellLayout}
                    />
                )}
            </div>
        )
    );
};

export default UpsellsComponent;
